// GroupsView.js - Grupper i huvudsidomenyn (Spår P + P2)
// Flik "Grupper": grupperna som entiteter - vald grupp visas som två kolumner
// (gruppen | väntelistan) där patienter dras mellan kolumnerna (drag & drop).
// Väntelistan finns automatiskt för varje grupp. Flik "Gruppbokningar":
// aktiva gruppbokningar i kalendern med deltagare.

import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import {
  getGroupAppointments,
  addGroupParticipant,
  removeGroupParticipant,
  getPatients,
  getGroupTemplates,
  createGroupTemplate,
  updateGroupTemplate,
  deleteGroupTemplate,
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
  moveWaitlistToMember,
  moveMemberToWaitlist,
  getWaitlist,
  addToWaitlist,
  removeFromWaitlist,
  updateWaitlistComment,
  getAppointmentTypes
} from './calendarApi';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { useScopeChoice } from './ScopeChoiceDialog';
import './GroupsView.css';

const RECURRENCE_LABELS = {
  daily: 'Upprepas dagligen',
  weekly: 'Upprepas varje vecka',
  biweekly: 'Upprepas varannan vecka'
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('sv-SE', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('sv-SE') : '–');

// Delat sökfält: sök patient, exkludera redan tillagda, välj med klick
const PatientSearchField = ({ excludeIds, onSelect, placeholder, disabled }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  // Stabil nyckel i stället för arrayidentiteten - annars startar varje
  // förälder-render (t.ex. dragOver-uppdateringar) om debouncen
  const excludeKey = excludeIds.join(',');

  useEffect(() => {
    const search = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      const excluded = excludeKey ? excludeKey.split(',').map(Number) : [];
      try {
        const response = await getPatients(query, { limit: 20 });
        const patients = response.patients || response;
        setResults(
          (Array.isArray(patients) ? patients : [])
            .filter(p => !excluded.includes(p.patient_id))
        );
      } catch (err) {
        console.error('Error searching patients:', err);
      }
    };
    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [query, excludeKey]);

  const handleSelect = (patient) => {
    setQuery('');
    setResults([]);
    onSelect(patient);
  };

  return (
    <div className="gv-search">
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={e => setQuery(e.target.value)}
        disabled={disabled}
      />
      {results.length > 0 && (
        <div className="gv-search-results">
          {results.map(patient => (
            <div
              key={patient.patient_id}
              className="gv-search-item"
              onClick={() => handleSelect(patient)}
            >
              <span className="gv-search-name">{patient.fullname}</span>
              <span className="gv-search-pnr">{patient.personnummer}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Flik 1: Grupper (entiteter med medlemmar + väntelista) ============

const GroupManager = () => {
  const { user } = useContext(UserContext);
  const canDelete = isClinicLevelAdmin(user);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Ny grupp / redigera grupp
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', max_participants: 10, appointment_type_id: ''
  });

  // Vald grupps innehåll
  const [members, setMembers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Drag & drop-state: vilken kolumn hovras med giltigt kort
  const [dragOver, setDragOver] = useState(null); // 'members' | 'waitlist' | null

  // Spegel av selectedId så att sena svar för ett TIDIGARE gruppval inte
  // skriver in fel grupps medlemmar/kö i kolumnerna
  const selectedIdRef = useRef(null);

  const loadGroups = useCallback(async () => {
    try {
      const data = await getGroupTemplates();
      setGroups(data || []);
      setError(null);
    } catch (err) {
      setError('Kunde inte ladda grupper');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (templateId) => {
    if (!templateId) return;
    try {
      setLoadingDetail(true);
      const [m, w] = await Promise.all([
        getGroupMembers(templateId),
        getWaitlist(templateId)
      ]);
      if (selectedIdRef.current !== templateId) return; // gruppen har bytts
      setMembers(m || []);
      setWaitlist(w || []);
    } catch (err) {
      console.error('Kunde inte ladda gruppens innehåll:', err);
    } finally {
      if (selectedIdRef.current === templateId) {
        setLoadingDetail(false);
      }
    }
  }, []);

  useEffect(() => {
    loadGroups();
    getAppointmentTypes()
      .then(data => setAppointmentTypes(data || []))
      .catch(err => console.error('Kunde inte ladda bokningstyper:', err));
  }, [loadGroups]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      setMembers([]);
      setWaitlist([]);
    }
  }, [selectedId, loadDetail]);

  const selectedGroup = groups.find(g => g.template_id === selectedId);

  const refresh = async () => {
    await Promise.all([loadGroups(), loadDetail(selectedId)]);
  };

  const handleApiError = (err, fallback) => {
    setError(err.response?.data?.error || fallback);
  };

  // --- Grupp-CRUD ---

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', max_participants: 10, appointment_type_id: '' });
    setShowForm(true);
  };

  const openEditForm = (group) => {
    setEditingId(group.template_id);
    setFormData({
      name: group.name,
      description: group.description || '',
      max_participants: group.max_participants || 10,
      appointment_type_id: group.appointment_type_id || ''
    });
    setShowForm(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Namn krävs');
      return;
    }
    try {
      setError(null);
      const dataToSend = {
        ...formData,
        appointment_type_id: formData.appointment_type_id || null
      };
      if (editingId) {
        await updateGroupTemplate(editingId, dataToSend);
      } else {
        const created = await createGroupTemplate(dataToSend);
        setSelectedId(created.template_id);
      }
      setShowForm(false);
      loadGroups();
    } catch (err) {
      handleApiError(err, 'Kunde inte spara gruppen');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Är du säker på att du vill ta bort gruppen "${group.name}"? Medlemmar och väntelista döljs också.`)) {
      return;
    }
    try {
      await deleteGroupTemplate(group.template_id);
      if (selectedId === group.template_id) setSelectedId(null);
      loadGroups();
    } catch (err) {
      handleApiError(err, 'Kunde inte ta bort gruppen');
    }
  };

  // --- Medlemmar & väntelista ---

  const handleAddMember = async (patient) => {
    try {
      setError(null);
      await addGroupMember(selectedId, patient.patient_id);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte lägga till medlem');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Ta bort ${member.patient?.fullname || 'patienten'} ur gruppen?`)) return;
    try {
      await removeGroupMember(selectedId, member.member_id);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte ta bort medlem');
    }
  };

  const handleAddToWaitlist = async (patient) => {
    try {
      setError(null);
      await addToWaitlist(selectedId, patient.patient_id);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte lägga till i väntelistan');
    }
  };

  const handleRemoveFromWaitlist = async (entry) => {
    if (!window.confirm(`Ta bort ${entry.patient?.fullname || 'patienten'} från väntelistan?`)) return;
    try {
      await removeFromWaitlist(selectedId, entry.waitlist_id);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte ta bort från väntelistan');
    }
  };

  // Kommentar på väntelistraden (Spår U3)
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  // Escape avbryter: inputen avmonteras och kan då trigga onBlur - spara inte
  const commentCancelledRef = useRef(false);

  const startEditComment = (entry) => {
    commentCancelledRef.current = false;
    setEditingCommentId(entry.waitlist_id);
    setCommentDraft(entry.comment || '');
  };

  const saveComment = async (entry) => {
    if (commentCancelledRef.current) return;
    const next = commentDraft.trim();
    setEditingCommentId(null);
    if (next === (entry.comment || '')) return;
    try {
      setError(null);
      const updated = await updateWaitlistComment(selectedId, entry.waitlist_id, next);
      setWaitlist(prev => prev.map(w => (w.waitlist_id === entry.waitlist_id ? { ...w, comment: updated.comment } : w)));
    } catch (err) {
      handleApiError(err, 'Kunde inte spara kommentaren');
    }
  };

  const moveToMembers = async (waitlistId) => {
    // Kommentaren följer med i bekräftelsen men sparas inte på medlemskapet
    const entry = waitlist.find(w => w.waitlist_id === waitlistId);
    if (entry?.comment && !window.confirm(
      `Flytta ${entry.patient?.fullname || 'patienten'} in i gruppen?

Kommentar från väntelistan: ${entry.comment}`
    )) return;
    try {
      setError(null);
      await moveWaitlistToMember(selectedId, waitlistId);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte flytta till gruppen');
    }
  };

  const moveToWaitlist = async (memberId) => {
    try {
      setError(null);
      await moveMemberToWaitlist(selectedId, memberId);
      await refresh();
    } catch (err) {
      handleApiError(err, 'Kunde inte flytta till väntelistan');
    }
  };

  // --- Drag & drop ---

  const handleDragStart = (e, type, id) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, id }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOver(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('text/plain'));
    } catch {
      return;
    }
    if (payload.type === 'waitlist' && targetColumn === 'members') {
      moveToMembers(payload.id);
    } else if (payload.type === 'member' && targetColumn === 'waitlist') {
      moveToWaitlist(payload.id);
    }
  };

  const allowDrop = (e, column) => {
    e.preventDefault();
    setDragOver(column);
  };

  // dragleave bubblar från kolumnens barn - släck bara markeringen när
  // pekaren faktiskt lämnar kolumnen, annars flimrar drop-highlighten
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(null);
    }
  };

  const excludeAll = useMemo(
    () => [...members.map(m => m.patient_id), ...waitlist.map(w => w.patient_id)],
    [members, waitlist]
  );

  if (loading) {
    return <div className="gv-loading">Laddar grupper...</div>;
  }

  return (
    <div>
      {error && <div className="gv-error">{error}</div>}

      {/* Grupplista + ny grupp */}
      <div className="gv-group-bar">
        {groups.map(group => (
          <button
            key={group.template_id}
            className={`gv-group-chip ${selectedId === group.template_id ? 'active' : ''}`}
            onClick={() => setSelectedId(
              selectedId === group.template_id ? null : group.template_id
            )}
          >
            <i className="fas fa-users"></i>
            {group.name}
            <span className="gv-chip-count">
              {group.member_count || 0}{group.max_participants ? `/${group.max_participants}` : ''}
            </span>
            {(group.waitlist_count || 0) > 0 && (
              <span className="gv-chip-count">{group.waitlist_count} i kö</span>
            )}
          </button>
        ))}
        <button className="gv-group-chip gv-new-group" onClick={openCreateForm}>
          <i className="fas fa-plus"></i> Ny grupp
        </button>
      </div>

      {/* Formulär för ny/redigera grupp */}
      {showForm && (
        <form className="gv-form" onSubmit={handleSubmitForm}>
          <h3>{editingId ? 'Redigera grupp' : 'Ny grupp'}</h3>
          <div className="gv-form-row">
            <div className="gv-form-group">
              <label>Namn *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="T.ex. Artrosskola, Stresshantering"
                autoFocus
              />
            </div>
            <div className="gv-form-group gv-form-group-small">
              <label>Max deltagare</label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.max_participants}
                onChange={e => setFormData(prev => ({ ...prev, max_participants: parseInt(e.target.value) || 10 }))}
              />
            </div>
            <div className="gv-form-group">
              <label>Bokningstyp (valfritt)</label>
              <select
                value={formData.appointment_type_id}
                onChange={e => setFormData(prev => ({ ...prev, appointment_type_id: e.target.value }))}
              >
                <option value="">-- Ingen koppling --</option>
                {appointmentTypes.map(type => (
                  <option key={type.type_id} value={type.type_id}>
                    {type.name} ({type.duration_minutes} min)
                  </option>
                ))}
              </select>
            </div>
            <div className="gv-form-group">
              <label>Beskrivning</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Valfri beskrivning"
              />
            </div>
          </div>
          <div className="gv-form-actions">
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Spara' : 'Skapa grupp'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              Avbryt
            </button>
          </div>
          <p className="gv-form-hint">
            En väntelista skapas automatiskt för varje grupp.
          </p>
        </form>
      )}

      {groups.length === 0 && !showForm && (
        <p className="gv-empty">Inga grupper än. Skapa din första med "Ny grupp".</p>
      )}

      {/* Vald grupp: två kolumner med drag & drop */}
      {selectedGroup && (
        <div className="gv-detail">
          <div className="gv-detail-header">
            <h3>{selectedGroup.name}</h3>
            {selectedGroup.description && (
              <span className="gv-detail-description">{selectedGroup.description}</span>
            )}
            <div className="gv-detail-actions">
              <button className="btn-icon" title="Redigera grupp" onClick={() => openEditForm(selectedGroup)}>
                <i className="fas fa-edit"></i>
              </button>
              {canDelete && (
                <button className="btn-icon btn-icon-danger" title="Ta bort grupp" onClick={() => handleDeleteGroup(selectedGroup)}>
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
          </div>

          <p className="gv-dnd-hint">
            <i className="fas fa-arrows-alt-h"></i> Dra patienter mellan kolumnerna, eller använd pilknapparna.
          </p>

          {loadingDetail ? (
            <div className="gv-loading">Laddar...</div>
          ) : (
            <div className="gv-columns">
              {/* Kolumn: gruppen (medlemmar) */}
              <div
                className={`gv-column ${dragOver === 'members' ? 'drag-over' : ''}`}
                onDragOver={e => allowDrop(e, 'members')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, 'members')}
              >
                <div className="gv-column-header">
                  <h4>{selectedGroup.name}</h4>
                  <span className="gv-column-count">
                    {members.length}{selectedGroup.max_participants ? `/${selectedGroup.max_participants}` : ''} medlemmar
                  </span>
                </div>
                <PatientSearchField
                  excludeIds={excludeAll}
                  onSelect={handleAddMember}
                  placeholder="Lägg till medlem..."
                />
                <div className="gv-cards">
                  {members.length === 0 ? (
                    <div className="gv-column-empty">
                      <i className="fas fa-inbox"></i> Inga medlemmar än
                    </div>
                  ) : members.map(member => (
                    <div
                      key={member.member_id}
                      className="gv-patient-card"
                      draggable
                      onDragStart={e => handleDragStart(e, 'member', member.member_id)}
                    >
                      <i className="fas fa-grip-vertical gv-grip"></i>
                      <div className="gv-patient-info">
                        <span className="gv-patient-name">{member.patient?.fullname || `Patient ${member.patient_id}`}</span>
                        <span className="gv-patient-date">Tillagd {formatDate(member.created_at)}</span>
                      </div>
                      <button
                        className="btn-icon btn-icon-small"
                        title="Flytta till väntelistan"
                        onClick={() => moveToWaitlist(member.member_id)}
                      >
                        <i className="fas fa-arrow-right"></i>
                      </button>
                      <button
                        className="btn-icon btn-icon-small btn-icon-danger"
                        title="Ta bort ur gruppen"
                        onClick={() => handleRemoveMember(member)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kolumn: väntelistan */}
              <div
                className={`gv-column gv-column-waitlist ${dragOver === 'waitlist' ? 'drag-over' : ''}`}
                onDragOver={e => allowDrop(e, 'waitlist')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, 'waitlist')}
              >
                <div className="gv-column-header">
                  <h4>{selectedGroup.name} – väntelista</h4>
                  <span className="gv-column-count">{waitlist.length} i kö</span>
                </div>
                <PatientSearchField
                  excludeIds={excludeAll}
                  onSelect={handleAddToWaitlist}
                  placeholder="Lägg till i väntelistan..."
                />
                <div className="gv-cards">
                  {waitlist.length === 0 ? (
                    <div className="gv-column-empty">
                      <i className="fas fa-inbox"></i> Ingen i väntelistan
                    </div>
                  ) : waitlist.map((entry, index) => (
                    <div
                      key={entry.waitlist_id}
                      className="gv-patient-card"
                      draggable
                      onDragStart={e => handleDragStart(e, 'waitlist', entry.waitlist_id)}
                    >
                      <i className="fas fa-grip-vertical gv-grip"></i>
                      <span className="gv-queue-pos">#{index + 1}</span>
                      <div className="gv-patient-info">
                        <span className="gv-patient-name">{entry.patient?.fullname || `Patient ${entry.patient_id}`}</span>
                        <span className="gv-patient-date">Tillagd {formatDate(entry.created_at)}</span>
                        {editingCommentId === entry.waitlist_id ? (
                          <input
                            className="gv-waitlist-comment-input"
                            autoFocus
                            maxLength={255}
                            placeholder="Kommentar, t.ex. Önskar delta efter jul"
                            value={commentDraft}
                            onChange={e => setCommentDraft(e.target.value)}
                            onBlur={() => saveComment(entry)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') {
                                commentCancelledRef.current = true;
                                setEditingCommentId(null);
                              }
                            }}
                          />
                        ) : entry.comment ? (
                          <span
                            className="gv-waitlist-comment"
                            title="Klicka för att redigera kommentaren"
                            onClick={() => startEditComment(entry)}
                          >
                            <i className="fas fa-comment"></i> {entry.comment}
                          </span>
                        ) : null}
                      </div>
                      {editingCommentId !== entry.waitlist_id && !entry.comment && (
                        <button
                          className="btn-icon btn-icon-small"
                          title="Lägg till kommentar"
                          onClick={() => startEditComment(entry)}
                        >
                          <i className="fas fa-comment-medical"></i>
                        </button>
                      )}
                      <button
                        className="btn-icon btn-icon-small"
                        title="Flytta in i gruppen"
                        onClick={() => moveToMembers(entry.waitlist_id)}
                      >
                        <i className="fas fa-arrow-left"></i>
                      </button>
                      <button
                        className="btn-icon btn-icon-small btn-icon-danger"
                        title="Ta bort från väntelistan"
                        onClick={() => handleRemoveFromWaitlist(entry)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ Flik 2: Gruppbokningar (aktiva bokningar i kalendern) ============

const ActiveGroups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [scopeDialog, askScope] = useScopeChoice();

  const loadGroups = useCallback(async () => {
    try {
      const data = await getGroupAppointments();
      setGroups(data || []);
      setError(null);
    } catch (err) {
      setError('Kunde inte ladda gruppbokningar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const expandedGroup = groups.find(g => g.appointment_id === expandedId);
  // Deltagare ändras på listans tillfälle (nästa) - varje tillfälle har egna
  // deltagarbokningar (Spår G); i en serie frågas omfång
  const targetId = expandedGroup?.next_appointment_id;
  const isRecurring = expandedGroup && RECURRENCE_LABELS[expandedGroup.recurrence_pattern];

  const handleAddParticipant = async (patient) => {
    if (!targetId) return;
    const scope = isRecurring ? await askScope(`Lägg till ${patient.fullname || 'deltagaren'}`) : 'this';
    if (!scope) return;
    try {
      await addGroupParticipant(targetId, patient.patient_id, null, scope);
      await loadGroups();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte lägga till deltagare');
    }
  };

  const handleRemoveParticipant = async (participant) => {
    const name = participant.patient?.fullname || 'deltagaren';
    let scope = 'this';
    if (isRecurring) {
      scope = await askScope(`Avboka ${name}`,
        'Bokningen avbokas och finns kvar i historiken. Genomförda, uteblivna och betalda tillfällen lämnas orörda.');
      if (!scope) return;
    } else if (!window.confirm(`Avboka ${name} från bokningen? Bokningen finns kvar i historiken som avbokad.`)) {
      return;
    }
    try {
      const res = await removeGroupParticipant(targetId, participant.patient_id, scope);
      await loadGroups();
      if (res?.kept) {
        alert(`${res.kept} tillfälle(n) är redan genomförda, uteblivna eller betalda och avbokades inte.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte avboka deltagaren');
    }
  };

  if (loading) {
    return <div className="gv-loading">Laddar gruppbokningar...</div>;
  }

  return (
    <div>
      {error && <div className="gv-error">{error}</div>}

      {groups.length === 0 ? (
        <p className="gv-empty">
          Inga aktiva gruppbokningar just nu. Gruppbokningar skapas i kalendern
          genom att välja en grupp.
        </p>
      ) : (
        <div className="gv-booking-cards">
          {groups.map(group => {
            const activeCount = (group.participants || [])
              .filter(p => p.status === 'confirmed').length;
            return (
              <div
                key={group.appointment_id}
                className={`gv-booking-card ${expandedId === group.appointment_id ? 'expanded' : ''}`}
              >
                <div
                  className="gv-booking-header"
                  onClick={() => setExpandedId(
                    expandedId === group.appointment_id ? null : group.appointment_id
                  )}
                >
                  <div className="gv-booking-title">
                    <i className="fas fa-users"></i>
                    <h4>{group.group_name || 'Gruppbokning'}</h4>
                    <span className="gv-column-count">{activeCount} deltagare</span>
                  </div>
                  <div className="gv-booking-meta">
                    <span>{formatDateTime(group.next_start_time || group.start_time)}</span>
                    {group.user_name && <span>· {group.user_name}</span>}
                    <i className={`fas fa-chevron-${expandedId === group.appointment_id ? 'up' : 'down'}`}></i>
                  </div>
                </div>

                {RECURRENCE_LABELS[group.recurrence_pattern] && (
                  <p className="gv-booking-recurrence">
                    <i className="fas fa-redo"></i>{' '}
                    {RECURRENCE_LABELS[group.recurrence_pattern]}
                    {group.recurrence_end_date &&
                      ` t.o.m. ${formatDate(group.recurrence_end_date)}`}
                  </p>
                )}

                {expandedId === group.appointment_id && (
                  <div className="gv-booking-participants">
                    <div className="gv-booking-participants-header">
                      <h5>Deltagare</h5>
                      {group.next_appointment_id ? (
                        <PatientSearchField
                          excludeIds={(group.participants || [])
                            .filter(p => p.status !== 'cancelled').map(p => p.patient_id)}
                          onSelect={handleAddParticipant}
                          placeholder="Lägg till deltagare..."
                        />
                      ) : (
                        <span className="gv-patient-date">Öppna tillfället i kalendern för att lägga till</span>
                      )}
                    </div>
                    {(group.participants || []).length === 0 ? (
                      <div className="gv-column-empty">
                        <i className="fas fa-inbox"></i> Inga deltagare än
                      </div>
                    ) : (
                      <div className="gv-cards">
                        {group.participants.map(participant => (
                          <div key={participant.id} className="gv-patient-card">
                            <div className="gv-patient-info">
                              <span className="gv-patient-name">
                                {participant.patient?.fullname || `Patient ${participant.patient_id}`}
                                {participant.status !== 'confirmed' && (
                                  <span className="gv-patient-status">
                                    {{ cancelled: ' (avbokad)', no_show: ' (uteblev)', attended: ' (närvarande)' }[participant.status] || ''}
                                  </span>
                                )}
                              </span>
                              <span className="gv-patient-date">
                                Tillagd {formatDate(participant.created_at)}
                              </span>
                            </div>
                            {participant.status !== 'cancelled' && (
                              <button
                                className="btn-icon btn-icon-small btn-icon-danger"
                                onClick={() => handleRemoveParticipant(participant)}
                                title="Avboka från bokningen (finns kvar i historiken)"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {scopeDialog}
    </div>
  );
};

const GroupsView = () => {
  const [activeTab, setActiveTab] = useState('groups');

  return (
    <div className="groups-view">
      <div className="groups-header">
        <h2>Grupper</h2>
        <p className="groups-description">
          Skapa grupper, hantera medlemmar och väntelistor. Gruppbokningar görs
          i kalendern genom att välja en grupp.
        </p>
      </div>

      <div className="groups-tabs">
        <button
          className={`groups-tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          <i className="fas fa-users"></i> Grupper
        </button>
        <button
          className={`groups-tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          <i className="fas fa-calendar-alt"></i> Gruppbokningar
        </button>
      </div>

      {activeTab === 'groups' ? <GroupManager /> : <ActiveGroups />}
    </div>
  );
};

export default GroupsView;
